import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as ViewOffIcon,
} from "@mui/icons-material";
import api from "../../services/api";

const StaffManagement = () => {
  const { storeId } = useParams();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // 直近の読み込み要求ID（古いレスポンスでstateを上書きしない）
  const latestRequestRef = useRef(0);
  const currentAbortRef = useRef(null);

  const getTranslatedText = (translationObj, defaultLang = "ja") => {
    if (!translationObj) return "";

    if (typeof translationObj !== "object") return translationObj;

    return (
      translationObj[defaultLang] ||
      translationObj.ja ||
      translationObj.en ||
      translationObj.zh ||
      ""
    );
  };

  const [formData, setFormData] = useState({
    name: "",
    loginId: "",
    password: "",
    phone: "",
    isActive: true,
  });

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchStaff = async () => {
    try {
      const reqId = ++latestRequestRef.current;
      setLoading(true);
      try {
        currentAbortRef.current?.abort();
      } catch {}
      const controller = new AbortController();
      currentAbortRef.current = controller;
      const response = await api.get(`/api/stores/${storeId}/staff`, {
        signal: controller.signal,
      });
      // 最新の要求でなければ無視
      if (latestRequestRef.current !== reqId) return;
      const data = response.data;
      if (data && Array.isArray(data)) {
        setStaff(data);
      } else {
        setStaff([]);
      }
    } catch (error) {
      console.error("スタッフデータの取得に失敗:", error);
      setStaff([]);
    } finally {
      // 最新の要求であればloadingを閉じる
      setLoading((prev) => {
        return false;
      });
      currentAbortRef.current = null;
    }
  };

  const handleOpenDialog = (staffMember = null) => {
    if (staffMember) {
      setSelectedStaff(staffMember);
      setFormData({
        name: getTranslatedText(staffMember.name),
        loginId: staffMember.loginId || "",
        password: "",
        phone: staffMember.phone || "",
        isActive: staffMember.isActive !== false,
      });
    } else {
      setSelectedStaff(null);
      setFormData({
        name: "",
        loginId: "",
        password: "",
        phone: "",
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStaff(null);
    setShowPassword(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const nameObj =
        selectedStaff && typeof selectedStaff.name === "object"
          ? { ...selectedStaff.name, ja: formData.name }
          : { ja: formData.name };

      const submitData = {
        ...formData,
        name: nameObj,
      };

      if (selectedStaff) {
        // パスワードが空の場合は更新データから除外
        if (!submitData.password) {
          delete submitData.password;
        }
        await api.put(
          `/api/stores/${storeId}/staff/${selectedStaff._id}`,
          submitData
        );
        showSnackbar("スタッフ情報を更新しました");
      } else {
        await api.post(`/api/stores/${storeId}/staff`, submitData);
        showSnackbar("スタッフを追加しました");
      }
      fetchStaff();
      handleCloseDialog();
    } catch (error) {
      if (error.response?.status === 400) {
        showSnackbar(error.response.data.message, "error");
      } else {
        showSnackbar("操作に失敗しました", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (window.confirm("本当にこのスタッフを削除しますか？")) {
      try {
        setDeletingId(staffId);
        await api.delete(`/api/stores/${storeId}/staff/${staffId}`);
        setStaff(staff.filter((s) => s._id !== staffId));
        showSnackbar("スタッフを削除しました");
      } catch (error) {
        showSnackbar("削除に失敗しました", "error");
      } finally {
        setDeletingId(null);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleStatusChange = async (staffId, isActive) => {
    try {
      await api.patch(`/api/stores/${storeId}/staff/${staffId}/status`, {
        isActive,
      });
      setStaff(
        staff.map((s) => (s._id === staffId ? { ...s, isActive: isActive } : s))
      );
      showSnackbar(`スタッフを${isActive ? "有効" : "無効"}にしました`);
    } catch (error) {
      showSnackbar("ステータスの更新に失敗しました", "error");
    }
  };

  return (
    <Box aria-busy={loading}>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5">スタッフ管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          スタッフを追加
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名前</TableCell>
              <TableCell>ログインID</TableCell>
              <TableCell>電話番号</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>最終ログイン</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : staff && Array.isArray(staff) ? (
              staff.map((staffMember) => (
                <TableRow key={staffMember._id}>
                  <TableCell>{getTranslatedText(staffMember.name)}</TableCell>
                  <TableCell>{staffMember.loginId}</TableCell>
                  <TableCell>{staffMember.phone}</TableCell>
                  <TableCell>
                    <Chip
                      label={staffMember.isActive ? "有効" : "無効"}
                      color={staffMember.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {staffMember.lastLogin
                      ? new Date(staffMember.lastLogin).toLocaleString()
                      : "未ログイン"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleOpenDialog(staffMember)}
                      disabled={Boolean(deletingId)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(staffMember._id)}
                      disabled={deletingId === staffMember._id}
                    >
                      {deletingId === staffMember._id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  スタッフデータがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedStaff ? "スタッフ情報を編集" : "新規スタッフを追加"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="名前"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="ログインID"
              value={formData.loginId}
              onChange={(e) =>
                setFormData({ ...formData, loginId: e.target.value })
              }
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="パスワード"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required={!selectedStaff}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              fullWidth
              label="電話番号"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              margin="normal"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  color="primary"
                />
              }
              label="アカウントを有効にする"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              キャンセル
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? (
                <CircularProgress color="inherit" size={18} />
              ) : selectedStaff ? (
                "更新"
              ) : (
                "追加"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffManagement;
